import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { createPortalSession } from "@/services/payments";

interface SubscriptionBannerProps {
  currentPeriodEnd?: string | null;
}

/**
 * Dismissible amber banner shown when a subscription is set to cancel
 * at the end of the current billing period (subscriptionState === 'canceling').
 */
export default function SubscriptionBanner({ currentPeriodEnd }: SubscriptionBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);

  if (dismissed) return null;

  const endDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const handleManage = async () => {
    try {
      setLoading(true);
      const { url } = await createPortalSession(window.location.href);
      window.location.href = url;
    } catch {
      // Portal session failed — fall back to subscribe page
      window.location.href = "/subscribe";
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2.5">
      <div className="mx-auto max-w-5xl flex items-center justify-between gap-3">
        <p className="text-sm text-amber-800">
          Your subscription{" "}
          {endDate ? (
            <>
              ends on <span className="font-semibold">{endDate}</span>
            </>
          ) : (
            "is ending soon"
          )}
          .{" "}
          <button
            onClick={handleManage}
            disabled={loading}
            className="font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Manage subscription"}
          </button>
        </p>
        <button
          onClick={() => setDismissed(true)}
          className="shrink-0 rounded p-1 text-amber-600 hover:bg-amber-100 hover:text-amber-800 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
